import type { ReactElement } from 'react';
// eslint-disable-next-line jira/restricted/react-18
import { createRoot, hydrateRoot } from 'react-dom/client';
import { enableHydrationStatReporting } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { initDevHydrationReporting } from './hydration-reporting.tsx';
import { hydrationOptions } from './hydration-options.tsx';

export const getIsSsrHydrationEnabled = () => {
	// We want only 1 source of truth for "should hydrate" (coming from the server),
	// but keep the fg('jfp-magma-force-react-hydrate-root') here for local dev convenience.
	return (
		(typeof window !== 'undefined' && window.SPA_STATE?.clientShouldHydrate) ||
		fg('jfp-magma-force-react-hydrate-root')
	);
};

export const renderReactApp = (
	children: ReactElement,
	{
		isSsrHydrationEnabled,
		container,
	}: {
		isSsrHydrationEnabled: boolean;
		/**
		 * The container to render the React app into.
		 */
		container: HTMLElement;
	},
) => {
	if (isSsrHydrationEnabled) {
		if (process.env.NODE_ENV === 'development') {
			// At least for now, this only has an effect in dev mode (due to our patch only being active in dev mode)
			initDevHydrationReporting(window);
		}
		if (fg('jfp-magma-3742-report-hydration-stats')) {
			enableHydrationStatReporting();
		}

		// hydrateRoot also enables concurrent mode, if you enable hydration on your route. It's also assumed you want and understand concurrent mode.
		hydrateRoot(container, children, hydrationOptions);
	} else {
		// If the route is not enabled for hydration but concurrent mode is enabled for this environment, render in concurrent mode
		const root = createRoot(container);
		root.render(children);
	}
};
